import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useProject } from '@contexts/Project.context';
import { getView3dManifest } from '@utils/api/view3d';

/**
 * Finds out whether the project has a 3D view and puts its manifest in the project context.
 *
 * The backend will describe the 3D view in `project.view3d`; until then the file service is probed
 * once for `{uuid}/3d/manifest.json`. One request, no retries, and nothing is shown for the (usual)
 * projects without one.
 *
 * @param {object|null} project - the project document from the project query
 */
export const useView3dManifest = project => {
	const { dispatch } = useProject();
	const uuid = project?.uuid;
	const view3d = project?.view3d;
	// Single-image projects have no photogrammetry to build a 3D view from
	const isOrthophoto = project?.image_mode !== 'single_image';
	const manifestKey = view3d?.manifest_path;
	// The backend may say there is nothing to look for
	const enabled =
		!!uuid && isOrthophoto && (!view3d || view3d.status === 'ready' || !!manifestKey);

	const query = useQuery({
		queryKey: ['view3dManifest', uuid, manifestKey ?? null, view3d?.updated_at ?? null],
		queryFn: () => getView3dManifest(uuid, manifestKey),
		enabled,
		retry: false,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	const { data, status, fetchStatus, error } = query;

	useEffect(() => {
		if (!enabled) {
			dispatch({ type: 'setView3d', payload: { manifest: null, manifestStatus: 'none' } });
			return;
		}
		if (status === 'pending') {
			dispatch({ type: 'setView3d', payload: { manifestStatus: 'loading' } });
		} else if (status === 'error') {
			console.warn('3D view: could not read the manifest', error);
			dispatch({ type: 'setView3d', payload: { manifest: null, manifestStatus: 'error' } });
		} else if (status === 'success') {
			dispatch({
				type: 'setView3d',
				payload: { manifest: data ?? null, manifestStatus: data ? 'ready' : 'none' },
			});
		}
	}, [enabled, status, fetchStatus, data, error, dispatch]);

	return query;
};
